import { useEffect, useRef, useState } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useSlotGroup,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';

/**
 * Weidner override of the Aster ConvenienceCallout. Identical to the parent
 * theme's component except the angled background band is painted in solid black
 * at 50% opacity instead of `--vibe-color-primary` at 77%, matching the black
 * scrim used by FloorplansList and the page heroes so the property photo reads
 * through under a neutral tint.
 */

// ============================================================================
// Slide-in-on-scroll hook
// ============================================================================

// Reveals an element once it scrolls into view, so the feature image can
// slide in from the left. Respects prefers-reduced-motion by revealing
// immediately without animating.
function useSlideInOnScroll<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion || typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setVisible(true);
            observer.disconnect();
            break;
          }
        }
      },
      { threshold: 0.2 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return { ref, visible };
}

// ============================================================================
// Slot group definitions (inline literals — required for schema extraction)
// ============================================================================

// Angled band photo sitting behind the 50% black overlay. Defaults to the
// property featured image (like FloorplansList) so a background always renders
// even when the photo gallery is sparse; the gallery sourceBinding still lets
// an operator swap in any gallery photo from the editor.
const backgroundDef: SlotGroupDefinition = {
  label: 'Background Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'property_featured_image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'property_featured_image_media.alt_text',
      defaultValue: 'Community background',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 2,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'Pick a photo of an outdoor amenity or shared outdoor space — e.g. a pool, courtyard, rooftop deck, grilling/BBQ area, garden, or patio. Prefer an outdoor common area over apartment interiors, floor-plan diagrams, or lobby/indoor shots.',
    context:
      'Full-bleed background band sitting behind a dark overlay and the Convenience Callout copy; it should read as an inviting outdoor amenity, not fine detail.',
    sourceCollection: 'photo_gallery',
  },
};

// Smaller feature photo, offset above the top angle of the band so it overlaps
// the band's top-left corner. Defaults to an early gallery index so the
// overlapping image reliably renders instead of a blank placeholder when the
// gallery is short; the sourceBinding still lets an operator pick any photo.
const featureImageDef: SlotGroupDefinition = {
  label: 'Feature Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'photo_gallery[1].image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'photo_gallery[1].alt_text',
      defaultValue: 'Amenity space',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 1,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'Pick a photo that shows the community — an amenity space, shared interior common area (lobby, lounge, clubroom, fitness center), or a distinctive exterior/building shot. Avoid floor-plan diagrams and empty unfurnished unit interiors.',
    context:
      'Smaller feature photo overlapping the top-left corner of the callout band, shown as a card-sized image beside the copy. Should differ from the full-bleed outdoor background band behind it, so prefer an interior common area or building shot rather than the same outdoor amenity.',
    sourceCollection: 'photo_gallery',
  },
};

const ctaDef: SlotGroupDefinition = {
  label: 'CTA Link',
  fields: {
    url: {
      label: 'Button URL',
      type: 'url',
      defaultValue: '/amenities',
      suggestedFields: ['property_apply_now_url', 'property_website_url'],
    },
    label: {
      label: 'Button Label',
      type: 'text',
      defaultValue: 'Discover More',
    },
    _target: {
      label: 'Link Target',
      type: 'text',
      defaultValue: '_self',
    },
  },
};

// Band with an angled top edge (rises to the right) and a straight bottom edge.
const BAND_CLIP = 'polygon(0 16%, 100% 0, 100% 100%, 0 100%)';

// ============================================================================
// Component
// ============================================================================

export function ConvenienceCallout() {
  const bg = useSlotGroup('conveniencecallout.background', backgroundDef);
  const feature = useSlotGroup('conveniencecallout.feature_image', featureImageDef);
  const cta = useSlotGroup('conveniencecallout.cta', ctaDef);
  const { ref: featureRef, visible: featureVisible } = useSlideInOnScroll<HTMLDivElement>();

  return (
    <section className="relative overflow-hidden bg-background py-20 md:py-28">
      {/* Angled background band with a 50% black overlay. */}
      <div className="absolute inset-x-0 top-0 bottom-0" style={{ clipPath: BAND_CLIP }}>
        <EditableSlotGroup groupPath="conveniencecallout.background" definition={backgroundDef}>
          <div className="absolute inset-0">
            {bg.image ? (
              <VibesitesImage
                url={bg.image}
                alt={bg.alt || ''}
                sizes="100vw"
                className="absolute inset-0 h-full w-full object-cover object-center"
              />
            ) : (
              <div className="absolute inset-0 bg-[var(--vibe-color-surface)]" />
            )}
            {/* Black overlay at 50% opacity — Weidner override of the theme
                primary-color scrim, matching FloorplansList and the page heroes
                so more of the property photo reads through. */}
            <div className="absolute inset-0 bg-black opacity-50" />
          </div>
        </EditableSlotGroup>
      </div>

      {/* Content grid: offset feature image on the left, copy on the right. */}
      <div className="relative mx-auto grid max-w-6xl grid-cols-1 items-center gap-10 px-6 md:grid-cols-2 md:gap-16">
        {/* Feature image lifted above the band's top-left angle. Slides in from
            the left once it scrolls into view. */}
        <div
          ref={featureRef}
          className="relative md:-mt-12 lg:-mt-20"
          style={{
            transform: featureVisible ? 'translateX(0)' : 'translateX(-60px)',
            opacity: featureVisible ? 1 : 0,
            transition: 'transform 800ms cubic-bezier(0.22, 1, 0.36, 1), opacity 800ms ease-out',
            willChange: 'transform, opacity',
          }}
        >
          <EditableSlotGroup groupPath="conveniencecallout.feature_image" definition={featureImageDef}>
            {feature.image ? (
              <VibesitesImage
                url={feature.image}
                alt={feature.alt || ''}
                sizes="(min-width: 768px) 45vw, 90vw"
                className="h-[320px] w-full object-cover object-center shadow-2xl md:h-[460px]"
              />
            ) : (
              <div className="h-[320px] w-full bg-[var(--vibe-color-surface)] shadow-2xl md:h-[460px]" />
            )}
          </EditableSlotGroup>
        </div>

        {/* Copy column. */}
        <div className="py-2 md:py-8">
          <RichTextContent
            path="conveniencecallout.subtitle"
            label="Subtitle"
            defaultValue="<p>Convenience All Around</p>"
            as="h2"
            className="font-heading text-3xl font-normal leading-tight text-white md:text-4xl"
          />

          <RichTextContent
            path="conveniencecallout.body"
            label="Paragraph"
            defaultValue='<p>At <span data-type="field-token" data-field="property_name"></span>, we understand that convenience is the hallmark of true luxury. From our thoughtfully designed common spaces to our attentive concierge services, every amenity is curated to ensure that your lifestyle is both seamless and extraordinary.</p>'
            as="div"
            className="cc-body-font font-body mt-6 max-w-xl text-base leading-relaxed text-primary-foreground opacity-95 md:text-lg"
          />

          <EditableSlotGroup groupPath="conveniencecallout.cta" definition={ctaDef}>
            <a
              href={cta.url || '/amenities'}
              target={cta._target || '_self'}
              className="mt-8 inline-flex items-center justify-center bg-[var(--vibe-color-secondary)] px-10 py-4 font-heading text-sm font-normal uppercase tracking-widest text-[var(--vibe-color-text-on-secondary)] shadow-md transition-colors hover:bg-white hover:text-[var(--vibe-color-secondary)]"
            >
              {cta.label || 'Discover More'}
            </a>
          </EditableSlotGroup>
        </div>
      </div>
      <style>{`
        /* Saved editor HTML on this token often carries an inline
           font-family from the previous Oswald / Lexend mix. Pin the
           paragraph and every rich-text child to the body token. */
        .cc-body-font,
        .cc-body-font * {
          font-family: var(--vibe-font-body) !important;
        }
      `}</style>
    </section>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'ConvenienceCallout',
  displayName: 'Convenience Callout',
  tokens: {
    'conveniencecallout.subtitle': {
      label: 'Subtitle',
      type: 'richtext',
      defaultValue: '<p>Convenience All Around</p>',
    },
    'conveniencecallout.body': {
      label: 'Paragraph',
      type: 'richtext',
      defaultValue:
        '<p>At <span data-type="field-token" data-field="property_name"></span>, we understand that convenience is the hallmark of true luxury. From our thoughtfully designed common spaces to our attentive concierge services, every amenity is curated to ensure that your lifestyle is both seamless and extraordinary.</p>',
    },
  },
  slotGroups: {
    'conveniencecallout.background': backgroundDef,
    'conveniencecallout.feature_image': featureImageDef,
    'conveniencecallout.cta': ctaDef,
  },
};

(ConvenienceCallout as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default ConvenienceCallout;
